import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Smartphone, Bell, Moon, Check, X } from 'lucide-react';
import { API_BASE_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './FollowSettings.css';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        width: 44, height: 26, borderRadius: 20, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: checked ? '#32614d' : '#dde4de', position: 'relative', flexShrink: 0, transition: 'background 0.15s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3, width: 20, height: 20, borderRadius: '50%',
        background: 'white', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

function Row({ icon, title, subtitle, right, borderBottom = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: borderBottom ? '1px solid #f0f0f0' : 'none', flexWrap: 'wrap' }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: '#f0f9f4', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

function NotificationSettings() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { fetchPrefs(); }, []);

  const fetchPrefs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/user/notification-preferences`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (res.ok && data.success) setPrefs(data.preferences);
      else setError(data.error || 'Failed to load notification preferences');
    } catch {
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const save = async (next) => {
    setPrefs(next);
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/user/notification-preferences`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMessage('Saved');
        setTimeout(() => setSuccessMessage(null), 2000);
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="follow-settings-container">
        <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="follow-settings-container">
      <button onClick={() => navigate('/settings')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#32614d', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ArrowLeft size={16} /> Back to Settings
      </button>

      <div className="settings-header">
        <h2>Notifications</h2>
        <p className="settings-subtitle">Choose which alerts you get, and when.</p>
      </div>

      {error && <div className="error-message"><X size={20} /><span>{localizeMessage(error)}</span></div>}
      {successMessage && <div className="success-message"><Check size={20} /><span>{successMessage}</span></div>}

      <div className="settings-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <Row
          icon={<Bell size={17} />}
          title="In-app notifications"
          subtitle="Show up in your notification bell"
          right={<Toggle checked={prefs.inApp} disabled={saving} onChange={(v) => save({ ...prefs, inApp: v })} />}
        />
        <Row
          icon={<Mail size={17} />}
          title="Email notifications"
          subtitle="Sent to your account email"
          right={<Toggle checked={prefs.email} disabled={saving} onChange={(v) => save({ ...prefs, email: v })} />}
        />
        <Row
          icon={<Smartphone size={17} />}
          title="Push notifications"
          subtitle="Sent to your phone"
          borderBottom={false}
          right={<Toggle checked={prefs.push} disabled={saving} onChange={(v) => save({ ...prefs, push: v })} />}
        />
      </div>

      <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
        <Row
          icon={<Moon size={17} />}
          title="Quiet hours"
          subtitle="Pause in-app notifications during these hours"
          borderBottom={prefs.quietHoursEnabled}
          right={<Toggle checked={prefs.quietHoursEnabled} disabled={saving} onChange={(v) => save({ ...prefs, quietHoursEnabled: v })} />}
        />
        {prefs.quietHoursEnabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#555' }}>From</span>
            <select value={prefs.quietHoursStart} disabled={saving} onChange={(e) => save({ ...prefs, quietHoursStart: parseInt(e.target.value, 10) })}
              style={{ padding: '7px 10px', border: '1.5px solid #dde4de', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}>
              {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <span style={{ fontSize: 13, color: '#555' }}>to</span>
            <select value={prefs.quietHoursEnd} disabled={saving} onChange={(e) => save({ ...prefs, quietHoursEnd: parseInt(e.target.value, 10) })}
              style={{ padding: '7px 10px', border: '1.5px solid #dde4de', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}>
              {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationSettings;
